import React, {
  memo,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import clsx from "clsx";
import posthog from "posthog-js";

import {
  ArrowTopRightOnSquareIcon,
  BookOpenIcon,
  EnvelopeIcon,
  LifebuoyIcon,
  PaperAirplaneIcon,
  Squares2X2Icon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import { CpuChipIcon } from "@heroicons/react/24/solid";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { BREV_SUPPORT_EMAIL } from "constants/index";
import { UserContext } from "contexts/UserContext";

interface ParamTypes {
  httpStatus?: string;
}

type HelpCenterView = "triage" | "brev";

type ResourceLink = {
  title: string;
  description: React.ReactNode;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  external?: boolean;
};

type TriageLink = ResourceLink & { id: string };
type ReferrerCategory = "direct" | "brev" | "nvidia_support" | "other";
type ResourceSection = "primary" | "support" | "triage";
type FooterDestination =
  | "nvidia_support"
  | "geforce_support"
  | "return_console";

const PRODUCT_QUERY_PARAM = "product";
const BREV_PRODUCT_QUERY_VALUE = "brev";
const TRIAGE_PAGE_TITLE = "Choose the Right NVIDIA Support | Brev";
const BREV_PAGE_TITLE = "NVIDIA Brev Help Center";
const FOOTER_RETURN_URL = "/";
const FOOTER_RETURN_LABEL = "Return to the Brev console";
const FOOTER_NVIDIA_SUPPORT_URL = "https://www.nvidia.com/en-us/support/";
const FOOTER_NVIDIA_CONSUMER_SUPPORT_URL =
  "https://www.nvidia.com/en-us/support/consumer/";
const FOOTER_GEFORCE_NOW_SUPPORT_URL =
  "https://www.nvidia.com/en-us/geforce-now/support/";
const NVIDIA_DEVELOPER_HUB_URL = "https://developer.nvidia.com/";
const NVIDIA_LAUNCHABLES_DOC_URL =
  "https://docs.nvidia.com/brev/latest/launchables.html";
const NVIDIA_QUICKSTART_DOC_URL =
  "https://docs.nvidia.com/brev/latest/quick-start.html";
const FOOTER_LINK_BASE_CLASS = "text-highlight underline";
const FOOTER_LINK_FOCUS_CLASS =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight";

const primaryResources: ResourceLink[] = [
  {
    title: "Quickstart: Launch Your First Instance",
    description:
      "Walk through creating an account, picking the right GPU, and deploying your first Brev workspace in minutes.",
    href: NVIDIA_QUICKSTART_DOC_URL,
    icon: LifebuoyIcon,
    external: true,
  },
  {
    title: "Launchables Guide",
    description:
      "Package GPU environments into shareable links with guided steps for files, runtime, and compute setup.",
    href: NVIDIA_LAUNCHABLES_DOC_URL,
    icon: Squares2X2Icon,
    external: true,
  },
  {
    title: "NVIDIA Brev Docs",
    description:
      "Quickstarts, guided flows, and platform concepts to get productive fast.",
    href: "https://docs.nvidia.com/brev/latest",
    icon: BookOpenIcon,
    external: true,
  },
  {
    title: "NVIDIA Developer Hub",
    description:
      "Explore documentation, SDKs, and training built for NVIDIA developers.",
    href: NVIDIA_DEVELOPER_HUB_URL,
    icon: CodeBracketIcon,
    external: true,
  },
];

const supportResources: ResourceLink[] = [
  {
    title: "Contact Brev Support",
    description: (
      <>
        Email the Brev team directly at <strong>{BREV_SUPPORT_EMAIL}</strong>. We
        respond during North America business hours.
      </>
    ),
    href: `mailto:${BREV_SUPPORT_EMAIL}`,
    icon: EnvelopeIcon,
  },
  {
    title: "Share Feedback or Feature Requests",
    description:
      "Open a ticket or upvote ideas so we can keep improving Brev for you.",
    href: "https://github.com/brevdev/feedback",
    icon: PaperAirplaneIcon,
    external: true,
  },
];

const triageLinks: TriageLink[] = [
  {
    id: "nvidia",
    title: "NVIDIA Product Support",
    description:
      "Drivers, hardware warranties, consumer GPUs, Shield, or other NVIDIA products.",
    href: FOOTER_NVIDIA_CONSUMER_SUPPORT_URL,
    icon: LifebuoyIcon,
  },
  {
    id: "geforce",
    title: "GeForce NOW Support",
    description:
      "Cloud gaming account, billing, and streaming questions for GeForce NOW members.",
    href: FOOTER_GEFORCE_NOW_SUPPORT_URL,
    icon: CpuChipIcon,
  },
];

const BREV_AFFILIATED_HOSTS = [
  "brev.nvidia.com",
  "build.nvidia.com",
  "developer.nvidia.com",
];

const getReferrerCategory = (referrer: string): ReferrerCategory => {
  if (!referrer) {
    return "direct";
  }

  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();

    if (!hostname.endsWith("nvidia.com")) {
      return "other";
    }

    if (
      BREV_AFFILIATED_HOSTS.some(
        (allowedHost) =>
          hostname === allowedHost || hostname.endsWith(`.${allowedHost}`),
      )
    ) {
      return "brev";
    }

    return "nvidia_support";
  } catch (error) {
    return "other";
  }
};

const getPreferredView = (
  triageEnabled: boolean,
  search: string,
  forceTriage: boolean,
): HelpCenterView => {
  const requestedView = new URLSearchParams(search)
    .get(PRODUCT_QUERY_PARAM)
    ?.toLowerCase();

  if (requestedView === BREV_PRODUCT_QUERY_VALUE) {
    return "brev";
  }

  if (forceTriage) {
    return "triage";
  }

  if (!triageEnabled) {
    return "brev";
  }

  return "triage";
};

const HelpPage: React.FC = () => {
  const { httpStatus } = useParams<ParamTypes>();
  const userContext = useContext(UserContext);
  const history = useHistory();
  const location = useLocation();

  const [referrerInfo] = useState(() => {
    const rawReferrer =
      typeof document !== "undefined" ? document.referrer : "";
    const referrerCategory = getReferrerCategory(rawReferrer);

    return {
      rawReferrer,
      referrerCategory,
      shouldForceTriage: referrerCategory === "nvidia_support",
    };
  });

  const { shouldForceTriage, referrerCategory, rawReferrer } = referrerInfo;

  const [view, setView] = useState<HelpCenterView>(() =>
    getPreferredView(true, location.search, shouldForceTriage),
  );

  const hasUserOverride = useMemo(() => {
    const productParam = new URLSearchParams(location.search)
      .get(PRODUCT_QUERY_PARAM)
      ?.toLowerCase();

    return productParam === BREV_PRODUCT_QUERY_VALUE;
  }, [location.search]);

  const flagState = "enabled";
  const pageViewTrackedRef = useRef(false);
  const previousActiveViewRef = useRef<HelpCenterView | null>(null);

  const preferredView = useMemo(
    () => getPreferredView(true, location.search, shouldForceTriage),
    [location.search, shouldForceTriage],
  );

  useEffect(() => {
    setView((current) => (current === preferredView ? current : preferredView));
  }, [preferredView]);

  const activeView = view;

  useEffect(() => {
    document.title = view === "triage" ? TRIAGE_PAGE_TITLE : BREV_PAGE_TITLE;
  }, [view]);

  useEffect(() => {
    if (pageViewTrackedRef.current) {
      return;
    }

    posthog.capture("Help Center Page Viewed", {
      view: activeView,
      referrerCategory,
      forcedByReferrer: shouldForceTriage,
      flagState,
      hasUserOverride,
      search: location.search || undefined,
      referrer: rawReferrer || undefined,
    });

    pageViewTrackedRef.current = true;
  }, [
    activeView,
    flagState,
    hasUserOverride,
    location.search,
    rawReferrer,
    shouldForceTriage,
    referrerCategory,
  ]);

  useEffect(() => {
    if (previousActiveViewRef.current === activeView) {
      return;
    }

    posthog.capture("Help Center View Rendered", {
      view: activeView,
      previousView: previousActiveViewRef.current,
      referrerCategory,
      forcedByReferrer: shouldForceTriage,
      flagState,
      hasUserOverride,
    });

    previousActiveViewRef.current = activeView;
  }, [
    activeView,
    flagState,
    hasUserOverride,
    shouldForceTriage,
    referrerCategory,
  ]);

  const handleSelectBrev = () => {
    if (view !== "brev") {
      posthog.capture("Help Center Select Brev Clicked", {
        currentView: view,
        targetView: "brev",
        referrerCategory,
        forcedByReferrer: shouldForceTriage,
        flagState,
        wasFlagLoading: false,
        hasUserOverride,
      });

      setView("brev");
      const nextSearchParams = new URLSearchParams(location.search);
      nextSearchParams.set(PRODUCT_QUERY_PARAM, BREV_PRODUCT_QUERY_VALUE);
      const nextSearch = nextSearchParams.toString();
      history.replace({
        pathname: location.pathname,
        search: nextSearch ? `?${nextSearch}` : "",
      });
    }
  };

  const handleTriageLinkClick = (link: TriageLink) => {
    posthog.capture("Help Center Triage Link Clicked", {
      linkId: link.id,
      linkHref: link.href,
      referrerCategory,
      forcedByReferrer: shouldForceTriage,
      flagState,
      hasUserOverride,
      view: activeView,
    });
  };

  const handleResourceLinkClick = (
    link: ResourceLink,
    section: ResourceSection,
  ) => {
    posthog.capture("Help Center Resource Link Clicked", {
      section,
      linkTitle: link.title,
      linkHref: link.href,
      external: Boolean(link.external),
      referrerCategory,
      forcedByReferrer: shouldForceTriage,
      flagState,
      hasUserOverride,
      view: activeView,
    });
  };

  const handleFooterNavigate = (destination: FooterDestination) => {
    posthog.capture("Help Center Footer Link Clicked", {
      destination,
      referrerCategory,
      forcedByReferrer: shouldForceTriage,
      flagState,
      hasUserOverride,
      view: activeView,
    });
  };

  if (activeView === "triage") {
    return (
      <div className="min-h-screen bg-baseBg text-primary">
        <main className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-4 pb-16 pt-6 sm:px-6 sm:pt-12 lg:px-8">
          <section
            className="rounded-3xl bg-primaryBg/80 px-6 sm:px-8"
            aria-labelledby="help-center-heading"
          >
            <header className="max-w-3xl">
              <p className="text-sm uppercase tracking-wide text-highlight">
                {BREV_PAGE_TITLE}
              </p>
              <h1
                id="help-center-heading"
                className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl"
              >
                Let’s get you to the right support team
              </h1>
              <p className="mt-4 text-lg text-secondary">
                We noticed customers sometimes land here while trying to reach
                other NVIDIA teams. Choose the product you need help with below
                so you can connect with specialists right away.
              </p>
            </header>
          </section>

          <section className="grid gap-4 md:grid-cols-2 py-6">
            {triageLinks.map((link) => (
              <ResourceCard
                key={link.id}
                title={link.title}
                description={link.description}
                icon={link.icon}
                href={link.href}
                external
                onClick={() => handleTriageLinkClick(link)}
              />
            ))}
          </section>

          <button
            type="button"
            onClick={handleSelectBrev}
            className={clsx(
              "w-full rounded-xl border border-primaryBorder bg-secondaryBg/50 px-6 py-4 text-center transition-colors",
              "hover:bg-secondaryBg/70 hover:border-highlight/50",
            )}
          >
            <span className="text-base font-medium text-primary">
              Continue to the {BREV_PAGE_TITLE} →
            </span>
          </button>

          <footer className="rounded-2xl border border-primaryBorder bg-primaryBg/80 p-6 text-sm text-secondary">
            NVIDIA Brev is part of the NVIDIA Developer ecosystem. We only
            handle Brev workspace, launchable, and billing questions. For
            warranties, consumer GPUs, GeForce NOW, or gaming PCs, follow the
            links above.
          </footer>

          <FooterLinks
            className="mt-8"
            supportHref={FOOTER_NVIDIA_CONSUMER_SUPPORT_URL}
            onNavigate={handleFooterNavigate}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-baseBg text-primary">
      <main className="mx-auto w-full max-w-6xl px-4 pb-16 pt-0 sm:px-6 sm:pt-4 lg:px-8 lg:pt-6">
        <section
          className="rounded-3xl bg-primaryBg/80 px-6 py-6 sm:px-8 sm:pb-8"
          aria-labelledby="brev-help-center-heading"
        >
          <header className="flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              {httpStatus && (
                <span className="inline-flex items-center rounded-full bg-highlight/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-highlight">
                  Status code {httpStatus}
                </span>
              )}
              <h1
                id="brev-help-center-heading"
                className="mt-2 text-4xl font-semibold tracking-tight text-primary sm:text-5xl"
              >
                {BREV_PAGE_TITLE}
              </h1>
              <p className="mt-4 text-base font-medium">
                {`Hey ${userContext?.me?.username || userContext?.me?.name || "there"} 👋`}{" "}
                We’re here to help you build, ship, and scale on NVIDIA Brev.
              </p>
              <p className="mt-3 text-base text-secondary">
                This help center focuses on GPU workspaces, launchables,
                billing, and developer productivity. For other NVIDIA products,
                visit the general NVIDIA or GeForce NOW support links below.
              </p>
              <p className="mt-3 text-base text-secondary">
                Looking for support for another NVIDIA product? Visit{" "}
                <span className="inline-flex items-center gap-1">
                  <a
                    href="https://www.nvidia.com/en-us/support/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-highlight underline decoration-highlight/30 underline-offset-2 transition-colors hover:decoration-highlight"
                  >
                    NVIDIA Support{" "}
                  </a>
                  <ArrowTopRightOnSquareIcon
                    className="h-4 w-4"
                    aria-hidden="true"
                  />
                </span>
              </p>
            </div>
            <div className="flex h-full w-full max-w-sm items-center justify-center rounded-2xl border border-primaryBorder bg-gradient-to-br from-highlight/12 via-primaryBg to-secondaryBg p-8 text-center text-secondary shadow-md shadow-black/15">
              <p className="text-sm leading-relaxed">
                <strong className="block text-base text-primary">
                  Brev Support Hours
                </strong>
                Monday – Friday, 9am–6pm PT (excluding US holidays). We’ll
                respond as quickly as we can.
              </p>
            </div>
          </header>
        </section>

        <section className="flex flex-col gap-8">
          <ResourceSection
            title="Start with these resources"
            description="Everything you need to explore NVIDIA Brev, launch environments, and stay productive."
            links={primaryResources}
            trackingSection="primary"
            onLinkClick={handleResourceLinkClick}
          />
          <ResourceSection
            title="Need to talk to the Brev team?"
            description="Reach us directly or collaborate with other developers who are using Brev."
            links={supportResources}
            trackingSection="support"
            onLinkClick={handleResourceLinkClick}
          />
        </section>

        <FooterLinks
          className="mt-16"
          supportHref={FOOTER_NVIDIA_SUPPORT_URL}
          onNavigate={handleFooterNavigate}
        />
      </main>
    </div>
  );
};

interface ResourceSectionProps {
  title: string;
  description: string;
  links: ResourceLink[];
  trackingSection: ResourceSection;
  onLinkClick?: (link: ResourceLink, section: ResourceSection) => void;
}

interface FooterLinksProps {
  className?: string;
  supportHref: string;
  onNavigate?: (destination: FooterDestination) => void;
}

const FooterLinks = memo<FooterLinksProps>(function FooterLinks({
  className,
  supportHref,
  onNavigate,
}) {
  return (
    <div className={clsx("text-center text-sm text-secondary", className)}>
      <p>
        Looking for someone else? Visit{" "}
        <a
          className={clsx(FOOTER_LINK_BASE_CLASS, FOOTER_LINK_FOCUS_CLASS)}
          href={supportHref}
          onClick={() => onNavigate?.("nvidia_support")}
        >
          NVIDIA Support
        </a>{" "}
        or{" "}
        <a
          className={clsx(FOOTER_LINK_BASE_CLASS, FOOTER_LINK_FOCUS_CLASS)}
          href={FOOTER_GEFORCE_NOW_SUPPORT_URL}
          onClick={() => onNavigate?.("geforce_support")}
        >
          GeForce NOW Support
        </a>
        .
      </p>
      <p className="mt-2">
        Prefer to head back?{" "}
        <a
          className={clsx(FOOTER_LINK_BASE_CLASS, FOOTER_LINK_FOCUS_CLASS)}
          href={FOOTER_RETURN_URL}
          onClick={() => onNavigate?.("return_console")}
        >
          {FOOTER_RETURN_LABEL}
        </a>
        .
      </p>
    </div>
  );
});

interface ResourceCardProps {
  title: string;
  description: React.ReactNode;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  href?: string;
  external?: boolean;
  onClick?: () => void;
}

const ResourceCard = memo<ResourceCardProps>(function ResourceCard({
  title,
  description,
  icon: Icon,
  href,
  external,
  onClick,
}) {
  return (
    <a
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className={clsx(
        "group flex h-full flex-col gap-4 rounded-xl border border-primaryBorder bg-primaryBg p-6 shadow-md shadow-black/10 transition-all duration-200",
        "hover:scale-[1.02] hover:border-highlight hover:shadow-xl hover:shadow-highlight/20",
        "active:scale-[0.98]",
      )}
    >
      <div className="flex items-start gap-4">
        <span className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg border border-highlight/20 bg-highlight/15 text-highlight transition-all group-hover:border-highlight/40 group-hover:bg-highlight/25">
          <Icon className="h-6 w-6" aria-hidden="true" />
        </span>
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2">
            <h3 className="text-base font-semibold text-primary transition-colors group-hover:text-highlight">
              {title}
            </h3>
            {external && (
              <ArrowTopRightOnSquareIcon
                className="mb-0.5 h-5 w-5 flex-shrink-0 text-highlight"
                aria-hidden="true"
              />
            )}
          </div>
          <p className="text-sm leading-relaxed text-secondary">
            {description}
          </p>
        </div>
      </div>
    </a>
  );
});

const ResourceSection = memo<ResourceSectionProps>(function ResourceSection({
  title,
  description,
  links,
  trackingSection,
  onLinkClick,
}) {
  return (
    <div className="px-8 pt-8">
      <h2 className="text-2xl font-semibold text-primary">{title}</h2>
      <p className="mt-2 text-sm text-secondary">{description}</p>

      <ul className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        {links.map((link) => (
          <li key={link.href}>
            <ResourceCard
              title={link.title}
              description={link.description}
              icon={link.icon}
              href={link.href}
              external={link.external}
              onClick={() => onLinkClick?.(link, trackingSection)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
});

export default HelpPage;
